import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { ChatMessage, Editor, Receiver } from "@/model/csr-chat";
import type { RootState } from "@/store/store";

type CsrChatState = {
  channelId: string;
  isSocketConnected: boolean;
  messages: Record<string, ChatMessage[]>;
  typingEditors: Record<string, Editor | null>;
  unreadCounts: number;
  pendingFiles: Record<string, File>;
  channelOldCounts: Record<string, number>;
  channelMessageRef2: Record<string, string>;
};

const initialState: CsrChatState = {
  channelId: "",
  isSocketConnected: false,
  messages: {},
  typingEditors: {},
  unreadCounts: 0,
  pendingFiles: {},
  channelOldCounts: {},
  channelMessageRef2: {},
};

const csrChatSlice = createSlice({
  name: "csrChat",
  initialState,
  reducers: {
    setChannelId: (state, action: PayloadAction<string>) => {
      state.channelId = action.payload;
    },
    setSocketConnected: (state, action: PayloadAction<boolean>) => {
      state.isSocketConnected = action.payload;
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      const msg = action.payload;
      const existing = state.messages[msg.chatChannel] ?? [];
      if (existing.some((m) => m._id === msg._id)) return;
      state.messages[msg.chatChannel] = [...existing, msg];
    },
    setMessages: (
      state,
      action: PayloadAction<{ channelId: string; msgs: ChatMessage[] }>,
    ) => {
      state.messages[action.payload.channelId] = action.payload.msgs;
    },
    mergeMessages: (
      state,
      action: PayloadAction<{ channelId: string; msgs: ChatMessage[] }>,
    ) => {
      const { channelId, msgs } = action.payload;
      const existing = state.messages[channelId] ?? [];
      const existingIds = new Set(existing.map((m) => m._id));
      const incoming = msgs.filter((m) => !existingIds.has(m._id));
      if (incoming.length === 0) return;
      const merged = [...existing, ...incoming].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      state.messages[channelId] = merged;
    },
    updateMessage: (
      state,
      action: PayloadAction<{
        chatChannel: string;
        messageId: string;
        content: string;
      }>,
    ) => {
      const { chatChannel, messageId, content } = action.payload;
      const existing = state.messages[chatChannel] ?? [];
      const idx = existing.findIndex((m) => m._id === messageId);
      if (idx === -1) return;
      state.messages[chatChannel] = existing.map((m, i) =>
        i === idx ? { ...m, message: content } : m,
      );
    },
    replaceMessage: (
      state,
      action: PayloadAction<{
        chatChannel: string;
        tempId: string;
        realMessage: ChatMessage;
      }>,
    ) => {
      const { chatChannel, tempId, realMessage } = action.payload;
      const existing = state.messages[chatChannel] ?? [];
      const idx = existing.findIndex((m) => m._id === tempId);
      if (idx === -1) return;
      const updated = [...existing];
      updated[idx] = realMessage;
      state.messages[chatChannel] = updated;
    },
    markMessageSeen: (
      state,
      action: PayloadAction<{
        chatChannel: string;
        messageId: string;
        receiver: Receiver[];
      }>,
    ) => {
      const { chatChannel, messageId, receiver } = action.payload;
      const existing = state.messages[chatChannel] ?? [];
      const idx = existing.findIndex((m) => m._id === messageId);
      if (idx === -1) return;
      state.messages[chatChannel] = existing.map((m, i) =>
        i === idx ? { ...m, receiver } : m,
      );
    },
    setTypingEditor: (
      state,
      action: PayloadAction<{ channelId: string; editor: Editor | null }>,
    ) => {
      state.typingEditors[action.payload.channelId] = action.payload.editor;
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCounts = action.payload;
    },
    incrementUnread: (state) => {
      state.unreadCounts += 1;
    },
    clearUnread: (state) => {
      state.unreadCounts = 0;
    },
    setChannelOldCount: (
      state,
      action: PayloadAction<{ channelId: string; count: number }>,
    ) => {
      state.channelOldCounts[action.payload.channelId] = action.payload.count;
    },
    setChannelMessageRef2: (
      state,
      action: PayloadAction<{ channelId: string; ref: string }>,
    ) => {
      state.channelMessageRef2[action.payload.channelId] = action.payload.ref;
    },
    setPendingFile: (
      state,
      action: PayloadAction<{ tempId: string; file: File }>,
    ) => {
      state.pendingFiles[action.payload.tempId] = action.payload.file;
    },
    removePendingFile: (state, action: PayloadAction<string>) => {
      delete state.pendingFiles[action.payload];
    },
    resetCsrChat: () => initialState,
  },
});

export const {
  setChannelId,
  setSocketConnected,
  addMessage,
  setMessages,
  mergeMessages,
  updateMessage,
  replaceMessage,
  markMessageSeen,
  setTypingEditor,
  setUnreadCount,
  incrementUnread,
  clearUnread,
  setChannelOldCount,
  setChannelMessageRef2,
  setPendingFile,
  removePendingFile,
  resetCsrChat,
} = csrChatSlice.actions;

export default csrChatSlice.reducer;

// Selectors
const EMPTY_MESSAGES: ChatMessage[] = [];
export const selectCsrMessages = (channelId: string) => (state: RootState) =>
  state?.csrChat?.messages[channelId] ?? EMPTY_MESSAGES;
export const selectCsrTypingEditor =
  (channelId: string) => (state: RootState) =>
    state?.csrChat?.typingEditors[channelId] ?? null;
export const selectCsrUnreadCount = (state: RootState) =>
  state?.csrChat?.unreadCounts;
export const selectCsrChannelId = (state: RootState) =>
  state?.csrChat?.channelId;
export const selectCsrIsConnected = (state: RootState) =>
  state?.csrChat?.isSocketConnected;
